import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

export interface PickableMember {
  id: string;
  nombre: string;
  color: string;
}

export interface PickableGroup {
  id: string;
  nombre: string;
  color: string;
  userIds: string[];
}

interface Props {
  // Set seleccionado (ids de perfil) — controlado por el padre.
  value: string[];
  onChange: (ids: string[]) => void;
  // Personas elegibles (miembros del equipo).
  members: PickableMember[];
  // Grupos personales del usuario, para añadir varias personas de golpe.
  groups?: PickableGroup[];
  placeholder?: string;
}

// Picker de seguidores: chips de los seleccionados + buscador en popover sobre
// los miembros del equipo. Controlado (value/onChange) para servir tanto al
// hilo (alta/baja inmediata vía diff en el padre) como al formulario de
// creación (acumula ids hasta guardar). Modelado en TagSelector.
export default function FollowerPicker({ value, onChange, members, groups, placeholder }: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const byId = new Map(members.map((m) => [m.id, m]));
  const selectedSet = new Set(value);
  const selectedMembers = value
    .map((id) => byId.get(id))
    .filter((m): m is PickableMember => m != null);

  const filtered = members
    .filter((m) => !selectedSet.has(m.id))
    .filter((m) => m.nombre.toLowerCase().includes(query.trim().toLowerCase()));

  const add = (id: string) => {
    onChange([...value, id]);
    setQuery('');
    inputRef.current?.focus();
  };
  const remove = (id: string) => onChange(value.filter((x) => x !== id));

  // Añade de un grupo solo a quienes siguen en el equipo y no están ya elegidos.
  const memberIds = new Set(members.map((m) => m.id));
  const addableFromGroup = (g: PickableGroup) =>
    g.userIds.filter((id) => memberIds.has(id) && !selectedSet.has(id));
  const addGroup = (g: PickableGroup) => {
    const toAdd = addableFromGroup(g);
    if (toAdd.length === 0) return;
    onChange([...value, ...toAdd]);
    inputRef.current?.focus();
  };
  const usableGroups = (groups ?? []).filter((g) => addableFromGroup(g).length > 0);

  return (
    <div ref={wrapRef} style={styles.wrap}>
      <div className="st-field" style={styles.chipsRow}>
        {selectedMembers.map((m) => (
          <span key={m.id} style={styles.chip}>
            <span style={{ ...styles.mono, background: m.color }}>
              {(m.nombre.charAt(0) || '?').toUpperCase()}
            </span>
            {m.nombre}
            <button
              type="button"
              onClick={() => remove(m.id)}
              style={styles.chipClose}
              title="Quitar seguidor"
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={query}
          placeholder={selectedMembers.length === 0 ? (placeholder ?? '+ Añadir seguidor') : ''}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          style={styles.input}
        />
      </div>

      {open && (filtered.length > 0 || usableGroups.length > 0) && (
        <div style={styles.popover}>
          {usableGroups.length > 0 && (
            <>
              <div style={styles.popoverLabel}>Tus grupos</div>
              {usableGroups.map((g) => (
                <button key={g.id} type="button" onClick={() => addGroup(g)} style={styles.popoverItem}>
                  <span style={{ ...styles.dot, background: g.color }} />
                  <span style={{ flex: 1 }}>{g.nombre}</span>
                  <span style={styles.groupCount}>+{addableFromGroup(g).length}</span>
                </button>
              ))}
              {filtered.length > 0 && <div style={styles.popoverLabel}>Personas</div>}
            </>
          )}
          {filtered.slice(0, 8).map((m) => (
            <button key={m.id} type="button" onClick={() => add(m.id)} style={styles.popoverItem}>
              <span style={{ ...styles.mono, background: m.color }}>
                {(m.nombre.charAt(0) || '?').toUpperCase()}
              </span>
              <span style={{ flex: 1 }}>{m.nombre}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  wrap: { position: 'relative', width: '100%' },
  chipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    alignItems: 'center',
    padding: '7px 10px',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    background: '#fff',
    minHeight: 42,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '3px 8px 3px 4px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    color: '#334155',
    border: '1px solid #e2e8f0',
    background: '#f8fafc',
  },
  mono: {
    width: 18,
    height: 18,
    minWidth: 18,
    borderRadius: 9,
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  chipClose: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: 14,
    padding: 0,
    lineHeight: 1,
    color: '#94a3b8',
  },
  input: {
    flex: 1,
    minWidth: 110,
    border: 'none',
    outline: 'none',
    fontSize: 13,
    padding: '4px 2px',
    background: 'transparent',
  },
  popover: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    boxShadow: '0 8px 20px rgba(15,23,42,0.1)',
    zIndex: 50,
    maxHeight: 240,
    overflowY: 'auto',
  },
  popoverItem: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 13,
    color: '#0f172a',
    textAlign: 'left',
  },
  popoverLabel: {
    padding: '8px 12px 4px',
    fontSize: 11,
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  dot: {
    width: 10,
    height: 10,
    minWidth: 10,
    borderRadius: 5,
  },
  groupCount: {
    fontSize: 11,
    fontWeight: 700,
    color: '#0f766e',
  },
};
